/**
 * Adjusts an RGB color string so it remains visible against
 * the current background mode (dark or light).
 *
 * - In dark mode: boosts very dark colors so they stand out on dark backgrounds.
 * - In light mode: darkens very light/washed-out colors so they stand out on light backgrounds.
 * - Colors that are already comfortably visible in the current mode are returned unchanged.
 *
 * @param rgb  – comma-separated RGB string, e.g. "139, 195, 75"
 * @param isDark – true when dark mode is active
 * @returns adjusted comma-separated RGB string
 */
export function adaptColor(rgb: string, isDark: boolean): string {
  const parts = rgb.split(',').map(s => parseInt(s.trim()))
  if (parts.length < 3 || parts.some(isNaN)) return rgb

  let [r, g, b] = parts

  // --- Convert to HSL ---
  const rN = r / 255
  const gN = g / 255
  const bN = b / 255
  const max = Math.max(rN, gN, bN)
  const min = Math.min(rN, gN, bN)
  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case rN:
        h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6
        break
      case gN:
        h = ((bN - rN) / d + 2) / 6
        break
      case bN:
        h = ((rN - gN) / d + 4) / 6
        break
    }
  }

  // --- Adjust lightness based on mode ---
  if (isDark) {
    // Dark background (~#1e1e1e): colors with low lightness disappear.
    // Gently boost lightness of dark colors.
    const minL = 0.38
    if (l < minL) {
      // Lerp towards minL – stronger push the darker the color
      const t = (minL - l) / minL
      l = l + (minL - l) * (0.6 + 0.4 * t)
    }
    // Prevent blinding neon-white colors in dark mode
    const maxLDark = 0.78
    if (l > maxLDark) {
      const t = (l - maxLDark) / (1 - maxLDark)
      l = l - (l - maxLDark) * (0.5 + 0.5 * t)
    }
  } else {
    // Light background (~#ffffff): colors with very high lightness are washed out.
    const maxL = 0.62
    if (l > maxL) {
      const t = (l - maxL) / (1 - maxL)
      l = l - (l - maxL) * (0.6 + 0.4 * t)
    }
  }

  // Also ensure minimum saturation so grey-ish colors don't vanish
  // (skip for truly achromatic/grey presets – saturation < 5%)
  if (s > 0.05) {
    if (isDark && s < 0.25) {
      s = 0.25
    } else if (!isDark && s < 0.2) {
      s = 0.2
    }
  }

  // --- Convert back to RGB ---
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  let rOut: number, gOut: number, bOut: number
  if (s === 0) {
    rOut = gOut = bOut = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    rOut = hue2rgb(p, q, h + 1 / 3)
    gOut = hue2rgb(p, q, h)
    bOut = hue2rgb(p, q, h - 1 / 3)
  }

  return `${Math.round(rOut * 255)}, ${Math.round(gOut * 255)}, ${Math.round(bOut * 255)}`
}

/**
 * Converts a comma-separated RGB string (e.g. "139, 195, 75" or "rgb(139, 195, 75)")
 * or a Hex string into a valid 6-digit Hex string (e.g. "#8bc34a").
 * Defaults to brand green "#8bc34a" if invalid.
 */
export function rgbToHex(colorStr?: string | null): string {
  if (!colorStr) return '#8bc34a'
  const trimmed = colorStr.trim()
  if (trimmed.startsWith('#')) {
    if (trimmed.length === 7) return trimmed
    if (trimmed.length === 4) {
      return '#' + trimmed[1] + trimmed[1] + trimmed[2] + trimmed[2] + trimmed[3] + trimmed[3]
    }
  }
  const matches = trimmed.match(/\d+/g)
  if (!matches || matches.length < 3) return '#8bc34a'
  const r = Math.min(255, Math.max(0, parseInt(matches[0], 10)))
  const g = Math.min(255, Math.max(0, parseInt(matches[1], 10)))
  const b = Math.min(255, Math.max(0, parseInt(matches[2], 10)))
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
}

/**
 * Determines whether an RGB or Hex color string is perceptually light.
 * When true, dark text/icons (#2c3437) should be used for optimal contrast.
 * When false, light/white text/icons (#ffffff) should be used.
 *
 * Uses the standard YIQ perceived brightness formula with threshold 165.
 * This guarantees that brand green ("139, 195, 74", YIQ ~164.4) uses crisp white text,
 * while light yellows, pastels, limes, and creams (YIQ > 165) switch to dark text.
 */
export function isLightColor(colorStr?: string | null): boolean {
  if (!colorStr) return false
  const trimmed = colorStr.trim()
  let r = 0
  let g = 0
  let b = 0

  if (trimmed.startsWith('#')) {
    const hex = rgbToHex(trimmed).replace('#', '')
    r = parseInt(hex.substring(0, 2), 16)
    g = parseInt(hex.substring(2, 4), 16)
    b = parseInt(hex.substring(4, 6), 16)
  } else {
    const matches = trimmed.match(/\d+/g)
    if (!matches || matches.length < 3) return false
    r = Math.min(255, Math.max(0, parseInt(matches[0], 10)))
    g = Math.min(255, Math.max(0, parseInt(matches[1], 10)))
    b = Math.min(255, Math.max(0, parseInt(matches[2], 10)))
  }

  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq > 165
}

